import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, Button, Table, Form, Badge, Modal, Spinner } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const BoqAmendmentForm = () => {
  const { id } = useParams(); // for edit mode
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workOrderFromUrl = searchParams.get("workOrder") || "";
  const isEdit = Boolean(id);

  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState("");
  const [projectName, setProjectName] = useState("");
  const [amendmentStatus, setAmendmentStatus] = useState("Pending");
  const [amendmentNo, setAmendmentNo] = useState(null);
  const [boqItems, setBoqItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);

  // Modals for long item names and descriptions (identical to WorkOrderBOQ)
  const [itemNameModal, setItemNameModal] = useState({
    show: false,
    itemIndex: null,
    itemName: "",
    isNew: false,
  });

  const [descriptionModal, setDescriptionModal] = useState({
    show: false,
    itemIndex: null,
    itemName: "",
    description: "",
    isNew: false,
  });

  // Modal for adding a new BOQ Item
  const [showNewItemModal, setShowNewItemModal] = useState(false);
  const [newItemForm, setNewItemForm] = useState({
    wlino: "",
    hsnSac: "",
    itemName: "",
    description: "",
    unit: "",
    newQuantity: "",
    price: "",
  });

  useEffect(() => {
    fetchWorkOrders();
    fetchUnits();

    if (isEdit) {
      fetchAmendment();
    } else if (workOrderFromUrl) {
      setSelectedWO(workOrderFromUrl);
      loadBOQ(workOrderFromUrl);
    }
  }, [id]);

  // ---------------- FETCH WORK ORDERS ----------------
  const fetchWorkOrders = async () => {
    try {
      const res = await api.get("/work-orders?limit=1000");
      if (res.data.success) {
        setWorkOrders(res.data.data);
      }
    } catch {
      swal("Error", "Failed to load work orders", "error");
    }
  };

  const fetchUnits = async () => {
    try {
      const res = await api.get("/boq-unit-types");
      if (res.data.success) {
        setUnits(res.data.data);
      }
    } catch {
      swal("Error", "Failed to load BOQ units", "error");
    }
  };

  // ---------------- LOAD BOQ FOR NEW AMENDMENT ----------------
  const loadBOQ = async (woId) => {
    try {
      setLoading(true);
      const res = await api.get(`/work-orders/${woId}`);
      const wo = res.data?.data;

      if (!wo) {
        swal("Error", "Work order not found", "error");
        return;
      }

      setProjectName(
        wo.project?.projectCode
          ? `${wo.project.projectCode} – ${wo.project.projectName}`
          : wo.project?.projectName || wo.woNo || ""
      );

      setBoqItems(
        (wo.boqItems || []).map((item) => ({
          _id: item._id,
          wlino: item.wlino || "",
          hsnSac: item.hsnSac || "",
          itemName: item.itemName,
          description: item.description || "",
          unit: item.unit,
          oldQuantity: Number(item.quantity) || 0,
          newQuantity: Number(item.quantity) || 0,
          oldPrice: Number(item.price) || 0,
          price: Number(item.price) || 0,
          measurementType: item.measurementType || "quantity",
          isNew: false,
        }))
      );
    } catch {
      swal("Error", "Failed to load BOQ", "error");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- FETCH AMENDMENT FOR EDIT ----------------
  const fetchAmendment = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/boq-amendments/${id}`);
      if (res.data.success) {
        const data = res.data.data;
        const woObj = data.workOrder && typeof data.workOrder === "object" ? data.workOrder : null;
        const woId = woObj?._id || data.workOrder;
        setSelectedWO(woId);
        setRemarks(data.remarks || "");
        setAmendmentStatus(data.status || "Pending");
        setAmendmentNo(data.amendmentNo);

        let pName = "";
        if (woObj?.project) {
          pName = woObj.project.projectCode
            ? `${woObj.project.projectCode} – ${woObj.project.projectName || ""}`
            : woObj.project.projectName || woObj.woNo || "";
        }
        if (!pName && woObj?.woNo) pName = woObj.woNo;

        // Try to obtain original Work Order items so amendment can be edited in full context
        let woItems = woObj?.boqItems || [];
        if (!woItems.length && woId) {
          try {
            const woRes = await api.get(`/work-orders/${woId}`);
            const fetchedWo = woRes.data?.data;
            if (fetchedWo) {
              if (!pName) {
                pName = fetchedWo.project?.projectCode
                  ? `${fetchedWo.project.projectCode} – ${fetchedWo.project.projectName || ""}`
                  : fetchedWo.project?.projectName || fetchedWo.woNo || "";
              }
              woItems = fetchedWo.boqItems || [];
            }
          } catch (e) {
            console.error("Could not fetch work order details", e);
          }
        }
        setProjectName(pName);

        const amendMap = new Map();
        (data.items || []).forEach((it) => {
          const key = String(it.itemName || "").trim().toLowerCase();
          if (key) amendMap.set(key, it);
        });

        if (woItems.length > 0) {
          const merged = woItems.map((woItem) => {
            const key = String(woItem.itemName || "").trim().toLowerCase();
            const amended = amendMap.get(key);
            if (amended) {
              amendMap.delete(key);
              return {
                _id: woItem._id,
                wlino: amended.wlino || woItem.wlino || "",
                hsnSac: amended.hsnSac || woItem.hsnSac || "",
                itemName: woItem.itemName,
                description: amended.description || woItem.description || "",
                unit: amended.unit || woItem.unit,
                oldQuantity: Number(amended.oldQuantity !== undefined ? amended.oldQuantity : woItem.quantity) || 0,
                newQuantity: Number(amended.newQuantity !== undefined ? amended.newQuantity : woItem.quantity) || 0,
                oldPrice: Number(amended.oldPrice !== undefined ? amended.oldPrice : woItem.price) || 0,
                price: Number(amended.price !== undefined ? amended.price : woItem.price) || 0,
                measurementType: amended.measurementType || woItem.measurementType || "quantity",
                isNew: Boolean(amended.isNewItem || amended.isNew),
              };
            }
            return {
              _id: woItem._id,
              wlino: woItem.wlino || "",
              hsnSac: woItem.hsnSac || "",
              itemName: woItem.itemName,
              description: woItem.description || "",
              unit: woItem.unit,
              oldQuantity: Number(woItem.quantity) || 0,
              newQuantity: Number(woItem.quantity) || 0,
              oldPrice: Number(woItem.price) || 0,
              price: Number(woItem.price) || 0,
              measurementType: woItem.measurementType || "quantity",
              isNew: false,
            };
          });

          // Append any newly added items from the amendment
          amendMap.forEach((extraItem) => {
            merged.push({
              _id: extraItem._id,
              wlino: extraItem.wlino || "",
              hsnSac: extraItem.hsnSac || "",
              itemName: extraItem.itemName,
              description: extraItem.description || extraItem.itemName,
              unit: extraItem.unit,
              oldQuantity: Number(extraItem.oldQuantity) || 0,
              newQuantity: Number(extraItem.newQuantity) || 0,
              oldPrice: Number(extraItem.oldPrice) || 0,
              price: Number(extraItem.price) || 0,
              measurementType: extraItem.measurementType || "quantity",
              isNew: Boolean(extraItem.isNewItem ?? extraItem.isNew ?? true),
            });
          });

          setBoqItems(merged);
        } else {
          // If woItems was empty, load directly from amendment items
          setBoqItems(
            (data.items || []).map((it) => ({
              _id: it._id,
              wlino: it.wlino || "",
              hsnSac: it.hsnSac || "",
              itemName: it.itemName,
              description: it.description || "",
              unit: it.unit,
              oldQuantity: Number(it.oldQuantity) || 0,
              newQuantity: Number(it.newQuantity) || 0,
              oldPrice: Number(it.oldPrice !== undefined ? it.oldPrice : it.price) || 0,
              price: Number(it.price) || 0,
              measurementType: it.measurementType || "quantity",
              isNew: Boolean(it.isNewItem || it.isNew),
            }))
          );
        }
      }
    } catch {
      swal("Error", "Failed to load amendment", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleWOChange = (e) => {
    const value = e.target.value;
    setSelectedWO(value);
    if (value) loadBOQ(value);
  };

  const updateQty = (index, value) => {
    const updated = [...boqItems];
    updated[index].newQuantity = value === "" ? "" : Number(value);
    setBoqItems(updated);
  };

  const updatePrice = (index, value) => {
    const updated = [...boqItems];
    updated[index].price = value === "" ? "" : Number(value);
    setBoqItems(updated);
  };

  const updateItemField = (index, field, value) => {
    const updated = [...boqItems];
    updated[index][field] = value;
    setBoqItems(updated);
  };

  // ---------------- NEW ITEM MODAL ----------------
  const resetNewItemForm = () => {
    setNewItemForm({
      wlino: "",
      hsnSac: "",
      itemName: "",
      description: "",
      unit: "",
      newQuantity: "",
      price: "",
    });
  };

  const openNewItemModal = () => {
    resetNewItemForm();
    setShowNewItemModal(true);
  };

  const updateNewItemForm = (field, value) => {
    setNewItemForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveNewItem = () => {
    if (
      !newItemForm.itemName ||
      !newItemForm.description ||
      !newItemForm.unit ||
      newItemForm.newQuantity === "" ||
      isNaN(Number(newItemForm.newQuantity)) ||
      Number(newItemForm.newQuantity) <= 0 ||
      newItemForm.price === "" ||
      isNaN(Number(newItemForm.price)) ||
      Number(newItemForm.price) < 0
    ) {
      swal(
        "Warning",
        "Item name, description, unit, quantity (> 0) and price (>= 0) are required",
        "warning"
      );
      return;
    }

    setBoqItems((items) => [
      ...items,
      {
        wlino: newItemForm.wlino,
        hsnSac: newItemForm.hsnSac,
        itemName: newItemForm.itemName,
        description: newItemForm.description,
        unit: newItemForm.unit,
        oldQuantity: 0,
        newQuantity: Number(newItemForm.newQuantity),
        oldPrice: 0,
        price: Number(newItemForm.price),
        measurementType: "quantity",
        isNew: true,
      },
    ]);

    setShowNewItemModal(false);
    resetNewItemForm();
  };

  const removeNewItem = (index) => {
    setBoqItems((items) => items.filter((_, itemIndex) => itemIndex !== index));
  };

  // ---------------- DESCRIPTION & ITEM NAME MODALS ----------------
  const openItemNameModal = (index) => {
    const item = boqItems[index];
    if (!item) return;
    setItemNameModal({
      show: true,
      itemIndex: index,
      itemName: item.itemName || "",
      isNew: Boolean(item.isNew),
    });
  };

  const saveItemNameModal = () => {
    if (itemNameModal.itemIndex !== null && itemNameModal.isNew) {
      updateItemField(itemNameModal.itemIndex, "itemName", itemNameModal.itemName);
    }
    setItemNameModal({ show: false, itemIndex: null, itemName: "", isNew: false });
  };

  const openDescriptionModal = (index) => {
    const item = boqItems[index];
    if (!item) return;
    setDescriptionModal({
      show: true,
      itemIndex: index,
      itemName: item.itemName || `Item #${index + 1}`,
      description: item.description || "",
      isNew: Boolean(item.isNew),
    });
  };

  const saveDescriptionModal = () => {
    if (descriptionModal.itemIndex !== null && descriptionModal.isNew) {
      updateItemField(descriptionModal.itemIndex, "description", descriptionModal.description);
    }
    setDescriptionModal({ show: false, itemIndex: null, itemName: "", description: "", isNew: false });
  };

  // ---------------- IMPACT & DIFFERENCE CALCULATIONS ----------------
  const calculateDiff = (item) =>
    (Number(item.newQuantity) || 0) - (Number(item.oldQuantity) || 0);

  const calculateImpact = (item) => {
    const oldQty = Number(item.oldQuantity) || 0;
    const newQty = Number(item.newQuantity) || 0;
    const oldP = Number(item.oldPrice !== undefined ? item.oldPrice : item.price) || 0;
    const newP = Number(item.price) || 0;
    return (newQty * newP) - (oldQty * oldP);
  };

  const totalImpact = useMemo(() => {
    return boqItems.reduce((acc, item) => acc + calculateImpact(item), 0);
  }, [boqItems]);

  const changedCount = useMemo(() => {
    return boqItems.filter(
      (item) =>
        item.isNew ||
        Number(item.newQuantity) !== Number(item.oldQuantity) ||
        Number(item.price) !== Number(item.oldPrice !== undefined ? item.oldPrice : item.price)
    ).length;
  }, [boqItems]);

  // ---------------- SUBMIT ----------------
  const handleSubmit = async () => {
    if (!selectedWO) return swal("Warning", "Please select a Work Order", "warning");

    if (isEdit && amendmentStatus !== "Pending") {
      return swal("Warning", `This amendment is already ${amendmentStatus} and cannot be modified`, "warning");
    }

    const incompleteNewItem = boqItems.find(
      (item) =>
        item.isNew &&
        (!item.itemName ||
          !item.description ||
          !item.unit ||
          item.newQuantity === "" ||
          isNaN(Number(item.newQuantity)) ||
          Number(item.newQuantity) <= 0 ||
          item.price === "" ||
          isNaN(Number(item.price)) ||
          Number(item.price) < 0)
    );

    if (incompleteNewItem) {
      return swal(
        "Warning",
        "For new BOQ items, Item Name, Description, Unit, Quantity (> 0) and Price (>= 0) are required",
        "warning"
      );
    }

    const invalidPrice = boqItems.find(
      (item) => item.price === "" || isNaN(Number(item.price)) || Number(item.price) < 0
    );
    if (invalidPrice) {
      return swal("Warning", `Please enter a valid price for "${invalidPrice.itemName}"`, "warning");
    }

    const invalidQty = boqItems.find(
      (item) => item.newQuantity === "" || isNaN(Number(item.newQuantity)) || Number(item.newQuantity) < 0
    );
    if (invalidQty) {
      return swal("Warning", `Please enter a valid quantity for "${invalidQty.itemName}"`, "warning");
    }

    const newItems = boqItems.filter((item) => item.isNew);
    const changedItems = boqItems
      .filter(
        (item) =>
          !item.isNew &&
          (Number(item.newQuantity) !== Number(item.oldQuantity) ||
            Number(item.price) !== Number(item.oldPrice !== undefined ? item.oldPrice : item.price))
      )
      .map((item) => ({
        wlino: item.wlino,
        hsnSac: item.hsnSac,
        itemName: item.itemName,
        description: item.description,
        unit: item.unit,
        oldQuantity: Number(item.oldQuantity) || 0,
        newQuantity: Number(item.newQuantity) || 0,
        oldPrice: Number(item.oldPrice !== undefined ? item.oldPrice : item.price) || 0,
        price: Number(item.price) || 0,
        difference: calculateDiff(item),
        impactAmount: calculateImpact(item),
        measurementType: item.measurementType || "quantity",
        isNewItem: false,
      }));

    const submittedNewItems = newItems.map((item) => ({
      wlino: item.wlino,
      hsnSac: item.hsnSac,
      itemName: item.itemName,
      description: item.description,
      unit: item.unit,
      oldQuantity: 0,
      newQuantity: Number(item.newQuantity) || 0,
      oldPrice: 0,
      price: Number(item.price) || 0,
      difference: Number(item.newQuantity) || 0,
      impactAmount: calculateImpact(item),
      measurementType: item.measurementType || "quantity",
      isNewItem: true,
    }));

    if (!changedItems.length && !submittedNewItems.length) {
      return swal("Warning", "No BOQ item changed or added", "warning");
    }

    try {
      const payload = {
        workOrder: selectedWO,
        items: [...changedItems, ...submittedNewItems],
        remarks,
      };

      if (isEdit) {
        await api.put(`/boq-amendments/${id}`, payload);
        swal("Success", "BOQ amendment updated successfully", "success");
      } else {
        await api.post("/boq-amendments", payload);
        swal("Success", "BOQ amendment submitted for approval", "success");
      }

      navigate(
        selectedWO
          ? `/boq/amendment/list?workOrder=${selectedWO}`
          : "/boq/amendment/list"
      );
    } catch (error) {
      swal(
        "Error",
        error.response?.data?.message || "Failed to save amendment",
        "error"
      );
    }
  };

  return (
        <div className="boq-amendment-page">
      <style>{`
        .content-body:has(.boq-amendment-page) > .container-fluid,
        .content-body .container-fluid:has(.boq-amendment-page) {
          padding-top: 14px !important;
          padding-left: 14px !important;
          padding-right: 14px !important;
        }
        .boq-amendment-page {
          width: 100%;
          padding: 0 !important;
          margin: 0 !important;
        }
        .table-responsive {
          overflow-x: auto;
        }
        .boq-table {
          width: 100%;
          min-width: 1720px;
          table-layout: fixed;
        }
        .boq-table thead th {
          background-color: #f8fafc !important;
          color: #334155 !important;
          font-weight: 600 !important;
          text-align: center !important;
          padding: 11px 8px !important;
          border-bottom: 2px solid #e2e8f0 !important;
          font-size: 13px;
        }
        .boq-table tbody td {
          padding: 8px 6px !important;
          vertical-align: middle !important;
          font-size: 13px;
        }
        .form-control, .form-select {
          border-radius: 6px;
          border: 1px solid #d1d5db;
          font-size: 13px;
        }
        .bg-modified-row {
          background-color: rgba(13, 110, 253, 0.04) !important;
        }
      `}</style>

      <PageTitle
        activeMenu={isEdit ? "Edit BOQ Amendment" : "Add BOQ Amendment"}
        motherMenu="Work Order"
      />

      <Row>
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-3 py-3">
              <div>
                <Card.Title className="mb-0 fw-bold">
                  {isEdit ? `Edit BOQ Amendment ${amendmentNo ? `#${amendmentNo}` : ""}` : "Create BOQ Amendment"}
                  {projectName ? ` – ${projectName}` : ""}
                </Card.Title>
                <div className="text-muted small mt-1">
                  Revise item quantities, prices, or add new BOQ items with cost impact analysis
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                {selectedWO && (
                  <>
                    <Badge bg="light" text="dark" className="border px-3 py-2 fw-semibold" style={{ fontSize: "12px" }}>
                      Total Items: <strong>{boqItems.length}</strong> | Modified:{" "}
                      <strong className={changedCount > 0 ? "text-primary" : "text-muted"}>
                        {changedCount}
                      </strong>
                    </Badge>

                    <Badge
                      bg={totalImpact > 0 ? "success" : totalImpact < 0 ? "danger" : "secondary"}
                      className="px-3 py-2 fw-semibold"
                      style={{ fontSize: "12px" }}
                    >
                      Net Impact: ₹ {totalImpact.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Badge>

                    {isEdit && (
                      <Badge
                        bg={
                          amendmentStatus === "Approved"
                            ? "success"
                            : amendmentStatus === "Rejected"
                            ? "danger"
                            : "warning"
                        }
                        className="px-3 py-2 fw-semibold"
                        style={{ fontSize: "12px" }}
                      >
                        Status: {amendmentStatus}
                      </Badge>
                    )}

                    {(!isEdit || amendmentStatus === "Pending") && (
                      <Button variant="primary" size="sm" className="shadow-sm" onClick={openNewItemModal}>
                        <i className="fa fa-plus me-1"></i> Add Item
                      </Button>
                    )}
                  </>
                )}

                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() =>
                    navigate(
                      selectedWO
                        ? `/boq/amendment/list?workOrder=${selectedWO}`
                        : "/boq/amendment/list"
                    )
                  }
                >
                  <i className="fas fa-arrow-left me-1"></i> Back
                </Button>
              </div>
            </Card.Header>

            <Card.Body className={selectedWO ? "p-0" : ""}>
              {/* SELECT WORK ORDER SECTION (Only when adding without preselected WO) */}
              {!isEdit && !workOrderFromUrl && (
                <div className="p-3 bg-white border-bottom">
                  <Row className="align-items-center">
                    <Col md={6}>
                      <Form.Group className="mb-0">
                        <Form.Label className="fw-semibold small mb-1">
                          Select Work Order *
                        </Form.Label>
                        <Form.Select
                          value={selectedWO}
                          onChange={handleWOChange}
                        >
                          <option value="">-- Choose Work Order --</option>
                          {workOrders.map((wo) => (
                            <option key={wo._id} value={wo._id}>
                              {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="success" />
                  <div className="mt-2 text-muted small">Loading BOQ details...</div>
                </div>
              ) : selectedWO ? (
                <>
                  <div className="table-responsive">
                    <Table bordered hover className="boq-table mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: "45px" }}>#</th>
                          <th style={{ width: "95px" }}>WLI No</th>
                          <th style={{ width: "110px" }}>HSN / SAC</th>
                          <th style={{ width: "250px" }}>Item Name</th>
                          <th style={{ width: "320px" }}>Description</th>
                          <th style={{ width: "85px" }}>UOM</th>
                          <th style={{ width: "110px" }}>Old Qty</th>
                          <th style={{ width: "125px" }}>New Qty</th>
                          <th style={{ width: "90px" }}>Qty Diff</th>
                          <th style={{ width: "120px" }}>Old Price</th>
                          <th style={{ width: "135px" }}>New Price</th>
                          <th style={{ width: "150px" }}>Cost Impact</th>
                          <th style={{ width: "65px" }}>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {boqItems.length === 0 ? (
                          <tr>
                            <td colSpan={13} className="text-center text-muted py-5">
                              No BOQ items found for this work order.
                            </td>
                          </tr>
                        ) : (
                          boqItems.map((item, i) => {
                            const diff = calculateDiff(item);
                            const impact = calculateImpact(item);
                            const oldPriceVal = Number(item.oldPrice !== undefined ? item.oldPrice : item.price) || 0;
                            const newPriceVal = Number(item.price) || 0;
                            const priceChanged = oldPriceVal !== newPriceVal;
                            const qtyChanged = diff !== 0;
                            const isModified = item.isNew || priceChanged || qtyChanged;
                            const isReadOnly = isEdit && amendmentStatus !== "Pending";

                            return (
                              <tr
                                key={i}
                                className={isModified ? "bg-modified-row" : ""}
                              >
                                <td className="text-center fw-bold">{i + 1}</td>
                                <td>{item.wlino || "—"}</td>
                                <td>{item.hsnSac || "—"}</td>

                                {/* ITEM NAME WITH VIEW/EDIT EYE BUTTON */}
                                <td>
                                  <div className="d-flex align-items-center gap-1">
                                    <div className="flex-grow-1 text-truncate" title={item.itemName}>
                                      <span className="fw-semibold text-dark">{item.itemName}</span>
                                      {item.isNew && (
                                        <Badge bg="success" className="ms-1" style={{ fontSize: "10px" }}>
                                          New
                                        </Badge>
                                      )}
                                    </div>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="px-2 d-flex align-items-center justify-content-center flex-shrink-0 shadow-none"
                                      style={{ fontSize: "11px", height: "30px", borderRadius: "4px" }}
                                      onClick={() => openItemNameModal(i)}
                                      title="View Full Item Name"
                                    >
                                      <i className="fas fa-eye" style={{ fontSize: "11px" }}></i>
                                    </Button>
                                  </div>
                                </td>

                                {/* DESCRIPTION WITH VIEW/EDIT EYE BUTTON */}
                                <td>
                                  <div className="d-flex align-items-center gap-1">
                                    <div
                                      className="flex-grow-1 text-muted text-truncate"
                                      style={{ fontSize: "12px" }}
                                      title={item.description}
                                    >
                                      {item.description || "—"}
                                    </div>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="px-2 d-flex align-items-center justify-content-center flex-shrink-0 shadow-none"
                                      style={{ fontSize: "11px", height: "30px", borderRadius: "4px" }}
                                      onClick={() => openDescriptionModal(i)}
                                      title="View Full Description"
                                    >
                                      <i className="fas fa-eye" style={{ fontSize: "11px" }}></i>
                                    </Button>
                                  </div>
                                </td>

                                {/* UOM */}
                                <td className="text-center">{item.unit || "—"}</td>

                                {/* OLD QUANTITY (READ-ONLY) */}
                                <td className="text-end text-muted fw-semibold">
                                  {Number(item.oldQuantity || 0).toLocaleString("en-IN")}
                                </td>

                                {/* NEW QUANTITY (FULLY EDITABLE IN ADD AND EDIT MODES) */}
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={item.newQuantity ?? ""}
                                    onChange={(e) => updateQty(i, e.target.value)}
                                    disabled={isReadOnly}
                                    className="text-end fw-bold"
                                    style={{ minWidth: "95px" }}
                                  />
                                </td>

                                {/* QUANTITY DIFFERENCE BADGE */}
                                <td className="text-center">
                                  <Badge
                                    bg={
                                      diff > 0
                                        ? "success"
                                        : diff < 0
                                        ? "danger"
                                        : "secondary"
                                    }
                                  >
                                    {diff > 0 ? `+${diff}` : diff}
                                  </Badge>
                                </td>

                                {/* OLD PRICE (READ-ONLY) */}
                                <td className="text-end text-muted fw-semibold">
                                  ₹ {oldPriceVal.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>

                                {/* NEW PRICE (FULLY EDITABLE IN ADD AND EDIT MODES) */}
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={item.price ?? ""}
                                    onChange={(e) => updatePrice(i, e.target.value)}
                                    disabled={isReadOnly}
                                    className="text-end fw-bold"
                                    style={{ minWidth: "105px" }}
                                  />
                                </td>

                                {/* COST IMPACT */}
                                <td
                                  className={`text-end fw-bold ${
                                    impact > 0
                                      ? "text-success"
                                      : impact < 0
                                      ? "text-danger"
                                      : "text-muted"
                                  }`}
                                >
                                  ₹ {impact.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>

                                {/* ACTION (DELETE BUTTON FOR NEW ITEMS) */}
                                <td className="text-center">
                                  {item.isNew && !isReadOnly ? (
                                    <Button
                                      variant="link"
                                      className="text-danger p-0 shadow-none"
                                      onClick={() => removeNewItem(i)}
                                      title="Remove New Item"
                                    >
                                      <i className="fas fa-trash-alt"></i>
                                    </Button>
                                  ) : (
                                    <span className="text-muted small">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>

                      {boqItems.length > 0 && (
                        <tfoot>
                          <tr className="bg-light">
                            <td colSpan="11" className="text-end fw-bold py-3" style={{ fontSize: "14px" }}>
                              Total Net Financial Impact:
                            </td>
                            <td
                              className={`text-end fw-bold py-3 ${
                                totalImpact > 0
                                  ? "text-success"
                                  : totalImpact < 0
                                  ? "text-danger"
                                  : "text-dark"
                              }`}
                              style={{ fontSize: "15px" }}
                            >
                              ₹ {totalImpact.toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td />
                          </tr>
                        </tfoot>
                      )}
                    </Table>
                  </div>

                  {/* REMARKS AND SUBMIT BAR */}
                  <div className="p-3 bg-white border-top">
                    <Row className="align-items-end">
                      <Col md={8}>
                        <Form.Group className="mb-0">
                          <Form.Label className="fw-semibold small">
                            Amendment Remarks / Justification
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Enter amendment justification, client approval reference, or notes..."
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            disabled={isEdit && amendmentStatus !== "Pending"}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4} className="d-flex flex-column align-items-md-end justify-content-end mt-3 mt-md-0">
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            onClick={() =>
                              navigate(
                                selectedWO
                                  ? `/boq/amendment/list?workOrder=${selectedWO}`
                                  : "/boq/amendment/list"
                              )
                            }
                          >
                            Cancel
                          </Button>

                          {(!isEdit || amendmentStatus === "Pending") && (
                            <Button variant="primary" className="fw-bold px-4" onClick={handleSubmit}>
                              <i className="fa fa-save me-1"></i>
                              {isEdit ? "Update Amendment" : "Submit for Approval"}
                            </Button>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="fa fa-arrow-up fa-2x mb-3 text-secondary"></i>
                  <h5>Please select a Work Order above to view and amend its BOQ.</h5>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ADD NEW BOQ ITEM MODAL */}
      <Modal show={showNewItemModal} onHide={() => setShowNewItemModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="fw-bold" style={{ fontSize: "16px" }}>
            Add New BOQ Item
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Label className="small fw-semibold">WLI No</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. 1.01"
                  value={newItemForm.wlino}
                  onChange={(e) => updateNewItemForm("wlino", e.target.value)}
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label className="small fw-semibold">HSN / SAC</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. 9954"
                  value={newItemForm.hsnSac}
                  onChange={(e) => updateNewItemForm("hsnSac", e.target.value)}
                />
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Item Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter item name"
                value={newItemForm.itemName}
                onChange={(e) => updateNewItemForm("itemName", e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter detailed description"
                value={newItemForm.description}
                onChange={(e) => updateNewItemForm("description", e.target.value)}
                required
              />
            </Form.Group>

            <Row>
              <Col md={4} className="mb-3">
                <Form.Label className="small fw-semibold">UOM *</Form.Label>
                <Form.Select
                  value={newItemForm.unit}
                  onChange={(e) => updateNewItemForm("unit", e.target.value)}
                  required
                >
                  <option value="">Select UOM</option>
                  {units.map((u) => (
                    <option key={u._id} value={u.symbol || u.name}>
                      {u.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label className="small fw-semibold">Quantity *</Form.Label>
                <Form.Control
                  type="number"
                  min="0.001"
                  step="any"
                  placeholder="Qty"
                  value={newItemForm.newQuantity}
                  onChange={(e) => updateNewItemForm("newQuantity", e.target.value)}
                  required
                />
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label className="small fw-semibold">Price / Rate (₹) *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Price"
                  value={newItemForm.price}
                  onChange={(e) => updateNewItemForm("price", e.target.value)}
                  required
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="secondary" size="sm" onClick={() => setShowNewItemModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={saveNewItem}>
            Add Item to BOQ
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ITEM NAME VIEW / EDIT MODAL */}
      <Modal show={itemNameModal.show} onHide={() => setItemNameModal({ show: false, itemIndex: null, itemName: "", isNew: false })} centered>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="fw-bold" style={{ fontSize: "15px" }}>
            {itemNameModal.isNew ? "Edit Item Name" : "Item Name"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {itemNameModal.isNew ? (
            <Form.Group>
              <Form.Label className="small fw-semibold">Item Name</Form.Label>
              <Form.Control
                type="text"
                value={itemNameModal.itemName}
                onChange={(e) => setItemNameModal((prev) => ({ ...prev, itemName: e.target.value }))}
              />
            </Form.Group>
          ) : (
            <div className="p-3 bg-light rounded text-dark fw-semibold" style={{ whiteSpace: "pre-wrap" }}>
              {itemNameModal.itemName}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="secondary" size="sm" onClick={() => setItemNameModal({ show: false, itemIndex: null, itemName: "", isNew: false })}>
            Close
          </Button>
          {itemNameModal.isNew && (
            <Button variant="primary" size="sm" onClick={saveItemNameModal}>
              Save Name
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* DESCRIPTION VIEW / EDIT MODAL */}
      <Modal show={descriptionModal.show} onHide={() => setDescriptionModal({ show: false, itemIndex: null, itemName: "", description: "", isNew: false })} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="fw-bold" style={{ fontSize: "15px" }}>
            Description – {descriptionModal.itemName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {descriptionModal.isNew ? (
            <Form.Group>
              <Form.Label className="small fw-semibold">Item Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={descriptionModal.description}
                onChange={(e) => setDescriptionModal((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Form.Group>
          ) : (
            <div className="p-3 bg-light rounded text-dark" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, maxHeight: "400px", overflowY: "auto" }}>
              {descriptionModal.description || "No description provided."}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="secondary" size="sm" onClick={() => setDescriptionModal({ show: false, itemIndex: null, itemName: "", description: "", isNew: false })}>
            Close
          </Button>
          {descriptionModal.isNew && (
            <Button variant="primary" size="sm" onClick={saveDescriptionModal}>
              Save Description
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BoqAmendmentForm;
